export const DEFAULT_VIGNETTE = {
    color: "#0b0d10",
    opacity: 92,
    height: 100,
};
export const DEFAULT_LOGO_POSITION = { x: 5, y: 4 };
export const DEFAULT_WELCOME_POSITION = { x: 5, y: 14 };
export const WELCOME_FONT_FAMILIES = {
    system: "-apple-system, BlinkMacSystemFont, 'Segoe UI', system-ui, sans-serif",
    sans: "'Inter Tight', 'Helvetica Neue', Arial, sans-serif",
    serif: "'Cormorant Garamond', Georgia, Cambria, 'Times New Roman', serif",
    mono: "'JetBrains Mono', 'SF Mono', Menlo, Consolas, monospace",
    cursive: "'Brush Script MT', 'Lucida Handwriting', cursive",
    display: "'Instrument Serif', Georgia, Impact, 'Arial Black', serif",
};
export const WELCOME_FONT_OPTIONS = [
    { key: "system", label: "System" },
    { key: "sans", label: "Sans-serif" },
    { key: "serif", label: "Serif" },
    { key: "mono", label: "Monospace" },
    { key: "cursive", label: "Cursive" },
    { key: "display", label: "Display" },
];
export const DEFAULT_TEXT_FONT = "system";
export const DEFAULT_TEXT_COLOR = "#ffffff";
export const DEFAULT_TEXT_SIZE = 24;
export const DEFAULT_CLOCK_SIZE = 32;
export const DEFAULT_IMAGE_SIZE = 8;
export const REFERENCE_CANVAS_WIDTH = 1920;
export function fontSizePx(pt, canvasWidth) {
    return (pt / REFERENCE_CANVAS_WIDTH) * canvasWidth;
}
export function interpolateContent(template, ctx) {
    var _a;
    return template.replace(/\{tileName\}/g, (_a = ctx.tileName) !== null && _a !== void 0 ? _a : "");
}
export const DEFAULT_TILE_BG_COLOR = "#000000";
export const DEFAULT_TILE_BG_OPACITY = 40;
export const DEFAULT_TILE_FOCUS_BG_COLOR = "#ffffff";
export const DEFAULT_TILE_FOCUS_BG_OPACITY = 14;
export const DEFAULT_TILE_TITLE_COLOR = "#ffffff";
export const DEFAULT_TILE_TITLE_OPACITY = 100;
export function hexWithOpacity(hex, opacityPct) {
    const clean = hex.replace(/^#/, "");
    const expanded = clean.length === 3
        ? clean
            .split("")
            .map((c) => c + c)
            .join("")
        : clean;
    const r = parseInt(expanded.slice(0, 2), 16) || 0;
    const g = parseInt(expanded.slice(2, 4), 16) || 0;
    const b = parseInt(expanded.slice(4, 6), 16) || 0;
    const a = Math.max(0, Math.min(100, opacityPct)) / 100;
    return `rgba(${r}, ${g}, ${b}, ${a})`;
}
export function effectiveIconSize(tile) {
    var _a;
    if (typeof tile.iconSize === "number") {
        return Math.max(20, Math.min(99, tile.iconSize));
    }
    const display = (_a = tile.display) !== null && _a !== void 0 ? _a : (tile.hideTitle === true ? "icon-only" : "both");
    return display === "icon-only" ? 70 : 45;
}
export function tileBg(tile, focused) {
    var _a, _b, _c, _d;
    const color = focused
        ? ((_a = tile.focusBgColor) !== null && _a !== void 0 ? _a : DEFAULT_TILE_FOCUS_BG_COLOR)
        : ((_b = tile.bgColor) !== null && _b !== void 0 ? _b : DEFAULT_TILE_BG_COLOR);
    const opacity = focused
        ? ((_c = tile.focusBgOpacity) !== null && _c !== void 0 ? _c : DEFAULT_TILE_FOCUS_BG_OPACITY)
        : ((_d = tile.bgOpacity) !== null && _d !== void 0 ? _d : DEFAULT_TILE_BG_OPACITY);
    return hexWithOpacity(color, opacity);
}
export function tileTitleColor(tile, focused) {
    var _a, _b, _c, _d;
    const color = focused
        ? ((_a = tile.focusTitleColor) !== null && _a !== void 0 ? _a : tile.titleColor)
        : tile.titleColor;
    if (!color)
        return null;
    const opacity = focused
        ? ((_c = (_b = tile.focusTitleOpacity) !== null && _b !== void 0 ? _b : tile.titleOpacity) !== null && _c !== void 0 ? _c : 100)
        : ((_d = tile.titleOpacity) !== null && _d !== void 0 ? _d : 100);
    return hexWithOpacity(color, opacity);
}
export const DEFAULT_GRID = {
    cols: 24,
    rows: 16,
    bounds: { x: 4, y: 30, w: 92, h: 65 },
    gap: 16,
};
export function roomHasIntegrationToken(room) {
    var _a;
    return Boolean(((_a = room.integrationId) === null || _a === void 0 ? void 0 : _a.trim()) || room.hasIntegrationId);
}
export const DAYS_OF_WEEK = [
    "mon",
    "tue",
    "wed",
    "thu",
    "fri",
    "sat",
    "sun",
];
export const DEFAULT_DOOR_SIGN_ORIENTATION = "landscape";
export function isDoorSignOrientation(v) {
    return v === "landscape" || v === "portrait";
}
export const DEFAULT_DOOR_SIGN_IDENTITY_MODE = "none";
export const DOOR_SIGN_STATUSES = [
    "none",
    "resting",
    "knock",
    "do-not-disturb",
    "out",
];
export function isDoorSignStatus(v) {
    return typeof v === "string" && DOOR_SIGN_STATUSES.includes(v);
}
export function isDoorSignIdentityMode(v) {
    return (typeof v === "string" &&
        ["none", "initials", "first-name", "first-initial", "full-name"].includes(v));
}
export function reduceDisplayName(fullName, mode) {
    var _a;
    const name = (fullName !== null && fullName !== void 0 ? fullName : "").trim().replace(/\s+/g, " ");
    if (!name || mode === "none")
        return "";
    const parts = name.split(" ");
    const first = (_a = parts[0]) !== null && _a !== void 0 ? _a : "";
    const last = parts.length > 1 ? parts[parts.length - 1] : "";
    const initial = (s) => (s ? `${s[0].toUpperCase()}.` : "");
    switch (mode) {
        case "full-name":
            return name;
        case "first-name":
            return first;
        case "first-initial":
            return last ? `${first} ${initial(last)}` : first;
        case "initials":
            return [first, last].map(initial).filter(Boolean).join("");
        default:
            return "";
    }
}
export const DEFAULT_CALL_RING_TIMEOUT_SEC = 40;
export const DEFAULT_CALL_ANNOUNCE_SECONDS = 5;
export function resolveCallPolicy(care) {
    var _a, _b;
    const c = (_a = care === null || care === void 0 ? void 0 : care.calls) !== null && _a !== void 0 ? _a : {};
    return {
        enabled: c.enabled === true,
        residentsMayCallOut: c.residentsMayCallOut === true,
        allowAutoAnswer: c.allowAutoAnswer === undefined
            ? (care === null || care === void 0 ? void 0 : care.callAutoAnswerEnabled) === true
            : c.allowAutoAnswer === true,
        quietHours: (_b = c.quietHours) !== null && _b !== void 0 ? _b : null,
        ringTimeoutSec: typeof c.ringTimeoutSec === "number" && Number.isFinite(c.ringTimeoutSec)
            ? c.ringTimeoutSec
            : DEFAULT_CALL_RING_TIMEOUT_SEC,
        maxCallDurationMin: typeof c.maxCallDurationMin === "number" &&
            Number.isFinite(c.maxCallDurationMin)
            ? c.maxCallDurationMin
            : null,
        announceSeconds: typeof c.announceSeconds === "number" &&
            Number.isFinite(c.announceSeconds)
            ? c.announceSeconds
            : DEFAULT_CALL_ANNOUNCE_SECONDS,
    };
}
export function emptyCareProfileInput() {
    return {
        allergies: [],
        isolation: null,
        fallRisk: false,
        diet: null,
        precautions: [],
        careTeam: [],
        notes: null,
        videoCallsDisabled: false,
    };
}
export const CALL_CONTACT_SCOPES = [
    "premise",
    "roomType",
    "person",
];
export function isCallContactScope(v) {
    return typeof v === "string" && CALL_CONTACT_SCOPES.includes(v);
}
export const CALL_CONTACT_KINDS = [
    "family",
    "staff",
    "service",
];
export function isCallContactKind(v) {
    return typeof v === "string" && CALL_CONTACT_KINDS.includes(v);
}
export function isCallChannelKind(v) {
    return (v === "web_push" ||
        v === "sms" ||
        v === "voip_ios" ||
        v === "fcm_android" ||
        v === "staff_push");
}
export const CALL_SESSION_STATES = [
    "ringing",
    "active",
    "ended",
];
export function isCallSessionState(v) {
    return typeof v === "string" && CALL_SESSION_STATES.includes(v);
}
export const CALL_END_REASONS = [
    "answered_then_hangup",
    "declined",
    "timeout",
    "busy",
    "policy_block",
    "error",
];
export function isCallEndReason(v) {
    return typeof v === "string" && CALL_END_REASONS.includes(v);
}
export const CALL_INITIATOR_KINDS = [
    "room",
    "phone",
    "staff",
];
export function isCallInitiatorKind(v) {
    return (typeof v === "string" && CALL_INITIATOR_KINDS.includes(v));
}
export function callDirectionOf(kind) {
    return kind === "room" ? "outbound" : "inbound";
}
export const CARE_TASK_CATEGORIES = [
    "assistance",
    "water",
    "restroom",
    "pain",
    "comfort",
    "other",
];
export function isCareTaskCategory(v) {
    return (typeof v === "string" && CARE_TASK_CATEGORIES.includes(v));
}
export const CARE_TASK_STATUSES = [
    "open",
    "acknowledged",
    "resolved",
];
export function isCareTaskStatus(v) {
    return typeof v === "string" && CARE_TASK_STATUSES.includes(v);
}
export const DEFAULT_SEGMENT = "hotel";
export const SCHEME_KEYS = [
    "atelier",
    "mosaic",
    "spectrum",
    "care",
    "aurelia",
    "marea",
    "nord",
    "terra",
    "tann",
];
export function isSchemeKey(value) {
    return !!value && SCHEME_KEYS.includes(value);
}
export const SCREEN_ANCHORS = [
    "top-left",
    "top-center",
    "top-right",
    "middle-left",
    "center",
    "middle-right",
    "bottom-left",
    "bottom-center",
    "bottom-right",
];
export const CUSTOM_ITEM_MAX = 4;
export const DEFAULT_TV_STREAMING_SLUG = "com.noriginmedia.nenda.nm";
export const TV_PLAYER_CHOICES = [
    "norigin",
    "foyer",
];
export const DEFAULT_TV_PLAYER = "norigin";
export function isTvPlayerChoice(v) {
    return v === "norigin" || v === "foyer";
}
export const DEFAULT_ALERT_OFFLINE_MINUTES = 60;
export const DEFAULT_HEALTH_ONLINE_WINDOW_SECONDS = 60;
export const DEFAULT_HEALTH_FAILED_COMMAND_WINDOW_HOURS = 24;
export const DEFAULT_PHOTO_RETENTION_MONTHS = 12;
export const DEFAULT_METERED_TARGET = {
    mode: "hours",
    hours: 40,
    uptimePercent: 90,
};
export const DEFAULT_METERED_PASS_THRESHOLD_PERCENT = 100;
export const DEFAULT_METERED_FLEET_GOAL_PERCENT = 90;
export const DEFAULT_CHANNEL_LOGO_OFFSET_X = 48;
export const DEFAULT_CHANNEL_LOGO_OFFSET_Y = 48;
export const DEFAULT_CHANNEL_LOGO_WIDTH = 220;
export const HEALTH_ALERT_TYPES = [
    "device_offline",
    "never_reported",
    "version_drift",
    "failed_commands",
    "calling_unavailable",
    "playback_failing",
    "signage_stalled",
];
export const ALERT_SUBJECT_OF = {
    device_offline: "device",
    never_reported: "device",
    version_drift: "premise",
    failed_commands: "premise",
    calling_unavailable: "premise",
    playback_failing: "premise",
    signage_stalled: "device",
};
export const ALERT_HAS_THRESHOLD = {
    device_offline: true,
    never_reported: true,
    version_drift: true,
    failed_commands: true,
    calling_unavailable: false,
    playback_failing: true,
    signage_stalled: false,
};
export const ALERT_CHANNELS = ["email", "push"];
export const ALERT_SCOPE_RANK = {
    global: 0,
    company: 1,
    premise: 2,
};
export const DEFAULT_ALERT_RULE_SPEC = {
    device_offline: { enabled: true, threshold: null },
    never_reported: { enabled: true, threshold: null },
    version_drift: { enabled: false, threshold: 1 },
    failed_commands: { enabled: false, threshold: 1 },
    calling_unavailable: { enabled: true, threshold: 1 },
    playback_failing: { enabled: false, threshold: 5 },
    signage_stalled: { enabled: false, threshold: 1 },
};
export const ALERT_THRESHOLD_MIN = {
    device_offline: 5,
    never_reported: 5,
    version_drift: 1,
    failed_commands: 1,
    calling_unavailable: 1,
    playback_failing: 1,
    signage_stalled: 1,
};
export const CARE_PREMISE_TYPES = ["HOSPITAL", "SENIOR_LIVING"];
export function isCarePremiseType(type) {
    return CARE_PREMISE_TYPES.includes(type !== null && type !== void 0 ? type : "");
}
export function isCarePremise(p) {
    return p.segment === "care" || isCarePremiseType(p.type);
}
export function isScreenOnline(s, windowMs = 5 * 60 * 1000) {
    if (!s.lastSeenAt)
        return false;
    const t = Date.parse(s.lastSeenAt);
    return Number.isFinite(t) && Date.now() - t < windowMs;
}
export function mainPage(cfg) {
    var _a;
    return (_a = cfg.pages.find((p) => p.isMain)) !== null && _a !== void 0 ? _a : cfg.pages[0];
}
